import React, { useCallback, useEffect, useRef, useState } from "react";
import { Navigate } from "react-router-dom";
import PageBreadcrumb from "../../common/PageBreadcrumb/PageBreadcrumb";
import useInfoAlert from "../../../hooks/useInfoAlert";
import InfoAlert from "../../common/InfoAlert";
import { useUserData } from "../../../hooks/useUserData";
import OrganizationLoginIpSection from "./OrganizationLoginIpSection";
import OrganizationCredentialPolicySection from "./OrganizationCredentialPolicySection";
import OrganizationRemindersSection from "./OrganizationRemindersSection";
import {
  deleteOrganizationLocation,
  fetchOrganizationSetup,
  parseOrganizationSetupResponse,
  parseSetupSaveResult,
  saveOrganizationSetup,
} from "../../../services/organizationSetupService";
import {
  getAllPermissionKeys,
  getSessionRoleName,
  hasPermission,
  isSessionSuperAdmin,
  isSystemAdministratorRole,
  parsePermissions,
} from "../../../utils/permissionUtils";
import { organizationHasEPlannerModule } from "../../../utils/orgModuleAccess";
import "./OrganizationSetup.scss";

/** Set true when Study locations tab should be shown again. */
const SHOW_STUDY_LOCATIONS_TAB = false;

const SETUP_SECTIONS = [
  {
    key: "details",
    label: "Organisation details",
    subtitle: "Name, country, timezone, clinic hours, booking defaults and archive retention",
    icon: "fas fa-building",
  },
  {
    key: "locations",
    label: "Study locations",
    subtitle: "Sites and locations for your organisation",
    icon: "fas fa-map-marker-alt",
  },
  {
    key: "networking",
    label: "Networking",
    subtitle: "Login IP allowlist and access rules",
    icon: "fas fa-network-wired",
  },
  {
    key: "security",
    label: "Credential policy",
    subtitle: "Credential expiry and first-login rules",
    icon: "fas fa-shield-alt",
  },
  {
    key: "reminders",
    label: "Reminders",
    subtitle: "Master on/off, daily run time, single or multiple ePlanner points",
    subtitleWithoutEPlanner: "Master on/off and daily run time for automatic reminders",
    icon: "fas fa-bell",
  },
];

const SLOT_OPTIONS = ["5", "15", "30", "60", "90", "120", "150", "180", "210", "240"];

const DEFAULT_TIMEZONE_ID = "GMT Standard Time";
const DEFAULT_COUNTRY_ISO2 = "GB";
const DEFAULT_CURRENCY_CODE = "GBP";
const DEFAULT_DIAL_CODE = "+44";

const DEFAULT_TIMEZONE_OPTIONS = [
  { value: "GMT Standard Time", label: "(UTC+00:00) United Kingdom (London)" },
];

const parseTimeForInput = (value) => {
  if (!value) return "";
  const parts = String(value).split(":");
  if (parts.length < 2) return "";
  return `${parts[0].padStart(2, "0")}:${parts[1].padStart(2, "0")}`;
};

const formatTimeForApi = (value) => {
  if (!value) return "";
  const parts = String(value).split(":");
  const hours = (parts[0] || "00").padStart(2, "0");
  const minutes = (parts[1] || "00").padStart(2, "0");
  return `${hours}:${minutes}:00`;
};

const emptyLocationRow = () => ({
  key: `new-${Date.now()}-${Math.random()}`,
  LocationId: "",
  LocationName: "",
});

const OrganizationSetup = () => {
  const { userData, loading: userLoading } = useUserData();
  const { alertState, showAlert, hideAlert } = useInfoAlert();

  const isSystemAdmin = isSystemAdministratorRole(getSessionRoleName(userData));
  const userPermissions = isSystemAdmin
    ? getAllPermissionKeys()
    : parsePermissions(userData?.Permission || "");
  const canAccess =
    isSystemAdmin || hasPermission(userPermissions, "AdminRole");

  const organizationId = userData?.OrganizationId || "";
  const staffId = userData?.StaffId || "";

  const [loading, setLoading] = useState(true);
  const [reloading, setReloading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deletingLocationId, setDeletingLocationId] = useState("");

  const [orgName, setOrgName] = useState("");
  const [countryIso2, setCountryIso2] = useState(DEFAULT_COUNTRY_ISO2);
  const [dialCode, setDialCode] = useState(DEFAULT_DIAL_CODE);
  const [timeZoneId, setTimeZoneId] = useState(DEFAULT_TIMEZONE_ID);
  const [timeZoneOptions, setTimeZoneOptions] = useState(DEFAULT_TIMEZONE_OPTIONS);
  const [currencyCode, setCurrencyCode] = useState(DEFAULT_CURRENCY_CODE);
  const [currencyLabel, setCurrencyLabel] = useState("GBP — British Pound Sterling (£)");
  const [currencySymbol, setCurrencySymbol] = useState("£");
  const [countryOptions, setCountryOptions] = useState([]);
  const [countryTimeZones, setCountryTimeZones] = useState([]);
  const [startTime, setStartTime] = useState("");
  const [endTime, setEndTime] = useState("");
  const [slotDuration, setSlotDuration] = useState("");
  const [bookingsPerSlot, setBookingsPerSlot] = useState("");
  const [retentionPeriodYears, setRetentionPeriodYears] = useState("10");
  const [locations, setLocations] = useState([emptyLocationRow()]);
  const [errors, setErrors] = useState({});
  const [activeSection, setActiveSection] = useState("details");

  const hasEPlannerModule = organizationHasEPlannerModule(userData);

  const visibleSections = (
    SHOW_STUDY_LOCATIONS_TAB
      ? SETUP_SECTIONS
      : SETUP_SECTIONS.filter((section) => section.key !== "locations")
  ).map((section) => {
    if (section.key !== "reminders") return section;
    return {
      ...section,
      subtitle: hasEPlannerModule
        ? section.subtitle
        : section.subtitleWithoutEPlanner || section.subtitle,
    };
  });

  const showAlertRef = useRef(showAlert);
  showAlertRef.current = showAlert;

  const loadSetup = useCallback(
    async ({ showBlockingLoader = true } = {}) => {
      if (!organizationId) return;
      if (showBlockingLoader) {
        setLoading(true);
      } else {
        setReloading(true);
      }
      try {
        const response = await fetchOrganizationSetup(organizationId, staffId);
        if (Number(response?.success) !== 1) {
          showAlertRef.current({
            type: "error",
            message: response?.message || "Could not load organisation setup.",
          });
          return;
        }

        const {
          setupRow,
          locations: orgLocations,
          countries,
          countryTimeZones: ctz,
        } = parseOrganizationSetupResponse(response);

        const mappedCountries = (countries || [])
          .map((row) => ({
            value: row.CountryIso2 || "",
            label: row.CountryName || row.CountryIso2 || "",
            dialCode: row.DialCode || "",
            defaultCurrencyCode: row.DefaultCurrencyCode || "",
            currencyName: row.CurrencyName || "",
            symbol: row.Symbol || "",
            defaultTimeZoneId: row.DefaultTimeZoneId || DEFAULT_TIMEZONE_ID,
          }))
          .filter((row) => row.value);
        setCountryOptions(mappedCountries);
        setCountryTimeZones(Array.isArray(ctz) ? ctz : []);

        const resolvedCountry =
          setupRow?.CountryIso2 || DEFAULT_COUNTRY_ISO2;
        const zonesForCountry = (Array.isArray(ctz) ? ctz : [])
          .filter((row) => (row.CountryIso2 || "") === resolvedCountry)
          .map((row) => ({
            value: row.TimeZoneId || "",
            label: row.DisplayName || row.TimeZoneId || "",
          }))
          .filter((row) => row.value);
        setTimeZoneOptions(
          zonesForCountry.length > 0 ? zonesForCountry : DEFAULT_TIMEZONE_OPTIONS,
        );

        if (setupRow) {
          const countryRow = mappedCountries.find((c) => c.value === resolvedCountry);
          const code =
            setupRow.CurrencyCode ||
            countryRow?.defaultCurrencyCode ||
            DEFAULT_CURRENCY_CODE;
          const symbol = countryRow?.symbol || setupRow.CurrencySymbol || "£";
          const name = countryRow?.currencyName || code;
          setOrgName(setupRow.OrgName || "");
          setCountryIso2(resolvedCountry);
          setDialCode(setupRow.DialCode || DEFAULT_DIAL_CODE);
          setTimeZoneId(setupRow.TimeZoneId || DEFAULT_TIMEZONE_ID);
          setCurrencyCode(code);
          setCurrencySymbol(symbol);
          setCurrencyLabel(`${code} — ${name}${symbol ? ` (${symbol})` : ""}`);
          setStartTime(parseTimeForInput(setupRow.AppointmentStartTime));
          setEndTime(parseTimeForInput(setupRow.AppointmentEndTime));
          setSlotDuration(
            setupRow.AppointmentSlotDuration != null
              ? String(setupRow.AppointmentSlotDuration)
              : "",
          );
          setBookingsPerSlot(
            setupRow.NoofBookingPerSlot != null
              ? String(setupRow.NoofBookingPerSlot)
              : "",
          );
          setRetentionPeriodYears(
            setupRow.RetentionPeriodYears != null &&
              setupRow.RetentionPeriodYears !== ""
              ? String(setupRow.RetentionPeriodYears)
              : "10",
          );
        }

        const mappedLocations = orgLocations.map((row) => ({
          key: row.LocationId || `loc-${row.LocationName}`,
          LocationId: row.LocationId || "",
          LocationName: row.LocationName || "",
        }));
        setLocations(
          mappedLocations.length > 0
            ? [...mappedLocations, emptyLocationRow()]
            : [emptyLocationRow()],
        );
      } catch (err) {
        showAlertRef.current({
          type: "error",
          message: err?.message || "Could not load organisation setup.",
        });
      } finally {
        if (showBlockingLoader) {
          setLoading(false);
        } else {
          setReloading(false);
        }
      }
    },
    [organizationId, staffId],
  );

  useEffect(() => {
    if (!SHOW_STUDY_LOCATIONS_TAB && activeSection === "locations") {
      setActiveSection("details");
    }
  }, [activeSection]);

  useEffect(() => {
    if (!canAccess || !organizationId) return;
    loadSetup({ showBlockingLoader: true });
  }, [canAccess, organizationId, loadSetup]);

  if (userLoading || !userData) {
    return (
      <div className="d-flex justify-content-center p-5">
        <div className="spinner-border" role="status" />
      </div>
    );
  }

  if (!canAccess) {
    return <Navigate to="/notification-center" replace />;
  }

  const applyCountryDefaults = (nextCountryIso2) => {
    const country = countryOptions.find((c) => c.value === nextCountryIso2);
    const zones = countryTimeZones
      .filter((row) => (row.CountryIso2 || "") === nextCountryIso2)
      .map((row) => ({
        value: row.TimeZoneId || "",
        label: row.DisplayName || row.TimeZoneId || "",
      }))
      .filter((row) => row.value);
    const code = country?.defaultCurrencyCode || DEFAULT_CURRENCY_CODE;
    const symbol = country?.symbol || "£";
    const name = country?.currencyName || code;
    setCountryIso2(nextCountryIso2);
    setDialCode(country?.dialCode || DEFAULT_DIAL_CODE);
    setCurrencyCode(code);
    setCurrencySymbol(symbol);
    setCurrencyLabel(`${code} — ${name}${symbol ? ` (${symbol})` : ""}`);
    setTimeZoneOptions(zones.length > 0 ? zones : DEFAULT_TIMEZONE_OPTIONS);
    const preferredZone =
      country?.defaultTimeZoneId ||
      zones.find((z) => z.value)?.value ||
      DEFAULT_TIMEZONE_ID;
    setTimeZoneId(preferredZone);
  };

  const validateForm = () => {
    const nextErrors = {};
    if (!orgName.trim()) nextErrors.orgName = "Organisation name is required.";
    if (!countryIso2) nextErrors.countryIso2 = "Organisation country is required.";
    if (!timeZoneId) nextErrors.timeZoneId = "Organisation timezone is required.";
    if (!startTime) nextErrors.startTime = "Clinic opening time is required.";
    if (!endTime) nextErrors.endTime = "Clinic closing time is required.";
    if (!slotDuration) nextErrors.slotDuration = "Default slot duration is required.";
    if (!bookingsPerSlot) {
      nextErrors.bookingsPerSlot = "Default bookings per slot is required.";
    }
    const retentionNum = Number(retentionPeriodYears);
    if (
      !retentionPeriodYears ||
      Number.isNaN(retentionNum) ||
      retentionNum < 1 ||
      retentionNum > 100
    ) {
      nextErrors.retentionPeriodYears =
        "Retention period must be between 1 and 100 years.";
    }
    if (startTime && endTime && startTime >= endTime) {
      nextErrors.endTime = "Closing time must be after opening time.";
    }
    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const getLocationPayload = () =>
    locations
      .map((row) => ({
        LocationId: row.LocationId || "",
        LocationName: row.LocationName.trim(),
      }))
      .filter((row) => row.LocationName);

  const hasDuplicateLocations = (list) => {
    const names = list.map((row) => row.LocationName.toLowerCase());
    return new Set(names).size !== names.length;
  };

  const handleAddLocationRow = (index) => {
    const name = locations[index]?.LocationName?.trim();
    if (!name) {
      showAlert({ type: "warning", message: "Enter a location name first." });
      return;
    }
    const existing = getLocationPayload();
    if (
      existing.filter((row) => row.LocationName.toLowerCase() === name.toLowerCase())
        .length > 1
    ) {
      showAlert({ type: "warning", message: "Cannot add duplicate locations." });
      return;
    }
    setLocations((prev) => {
      const next = [...prev];
      next.splice(index + 1, 0, emptyLocationRow());
      return next;
    });
  };

  const handleRemoveLocationRow = async (index) => {
    const row = locations[index];
    if (!row) return;

    if (row.LocationId) {
      setDeletingLocationId(row.LocationId);
      try {
        const response = await deleteOrganizationLocation({
          organizationId,
          createdBy: staffId,
          locationId: row.LocationId,
        });
        const data = response?.data || {};
        const result = Array.isArray(data.SuccessStory) ? data.SuccessStory[0] : null;
        if (Number(result?.Success) !== 1 && Number(response?.success) !== 1) {
          showAlert({
            type: "error",
            message: result?.MSG || response?.message || "Could not delete location.",
          });
          return;
        }
      } catch (err) {
        showAlert({
          type: "error",
          message: err?.message || "Could not delete location.",
        });
        return;
      } finally {
        setDeletingLocationId("");
      }
    }

    setLocations((prev) => {
      const next = prev.filter((_, i) => i !== index);
      return next.length ? next : [emptyLocationRow()];
    });
  };

  const handleSave = async () => {
    if (!validateForm()) return;

    const locationList = getLocationPayload();
    if (hasDuplicateLocations(locationList)) {
      showAlert({ type: "warning", message: "Cannot add duplicate locations." });
      return;
    }

    setSaving(true);
    try {
      const selectedCountry = countryOptions.find((c) => c.value === countryIso2);
      const response = await saveOrganizationSetup({
        OrganizationId: organizationId,
        CreatedBy: staffId,
        OrgName: orgName.trim(),
        OrgCountry: selectedCountry?.label || "",
        CountryIso2: countryIso2 || DEFAULT_COUNTRY_ISO2,
        CurrencyCode:
          selectedCountry?.defaultCurrencyCode ||
          currencyCode ||
          DEFAULT_CURRENCY_CODE,
        DialCode: dialCode || DEFAULT_DIAL_CODE,
        TimeZoneId: timeZoneId || DEFAULT_TIMEZONE_ID,
        AppointmentStartTime: formatTimeForApi(startTime),
        AppointmentEndTime: formatTimeForApi(endTime),
        AppointmentSlotDuration: Number(slotDuration),
        LabelDefaultTime: "00:05:00",
        NoofBookingPerSlot: Number(bookingsPerSlot),
        IsDefaultSlot: false,
        CurrencySymbol: selectedCountry?.symbol || currencySymbol || "£",
        RetentionPeriodYears: Number(retentionPeriodYears) || 10,
        LocationList: locationList,
      });

      const result = parseSetupSaveResult(response);
      if (result.success) {
        showAlert({ type: "success", message: result.message });
        await loadSetup({ showBlockingLoader: false });
      } else {
        showAlert({ type: "error", message: result.message });
      }
    } catch (err) {
      showAlert({
        type: "error",
        message: err?.message || "Could not save organisation setup.",
      });
    } finally {
      setSaving(false);
    }
  };

  const isBusy = saving || reloading || Boolean(deletingLocationId);

  const renderSaveButton = () => (
    <div className="d-flex justify-content-end mt-3">
      <button
        type="button"
        className="btn btn-primary"
        disabled={isBusy}
        onClick={handleSave}
      >
        {saving ? "Saving..." : "Update"}
        {!saving ? <i className="fas fa-save ms-2" /> : null}
      </button>
    </div>
  );

  return (
    <div className="user-mgmt-page capa-config-saas org-setup-saas">
      <InfoAlert isOpen={alertState.isOpen} onClose={hideAlert} {...alertState} />

      <PageBreadcrumb
        items={[
          { label: "Home", path: "/notification-center" },
          { label: "Organisation Setup" },
        ]}
        actions={[
          {
            icon: "fas fa-refresh",
            label: reloading ? "Refreshing..." : "Refresh",
            onClick: () => loadSetup({ showBlockingLoader: false }),
          },
        ]}
      />

      {loading ? (
        <div className="card shadow-sm">
          <div className="card-body text-muted">Loading organisation setup...</div>
        </div>
      ) : (
        <div className="row g-3 capa-config-saas__layout">
          <div className="col-md-4 col-xl-3 capa-config-saas__nav-col">
            <div className="card capa-config-saas__nav-card">
              <div className="list-group list-group-flush capa-config-saas__nav-list">
                {visibleSections.map((section) => (
                  <button
                    key={section.key}
                    type="button"
                    className={`list-group-item list-group-item-action capa-config-saas__nav-item ${
                      activeSection === section.key ? "active" : ""
                    }`}
                    onClick={() => setActiveSection(section.key)}
                    aria-current={activeSection === section.key ? "page" : undefined}
                  >
                    <div className="d-flex align-items-start gap-2">
                      <i className={`${section.icon} mt-1`} />
                      <div>
                        <div className="fw-semibold capa-config-saas__nav-item-title">
                          {section.label}
                        </div>
                        <div
                          className={`small capa-config-saas__nav-item-subtitle ${
                            activeSection === section.key ? "text-white-50" : "text-muted"
                          }`}
                        >
                          {section.subtitle}
                        </div>
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          </div>

          <div className="col-md-8 col-xl-9 capa-config-saas__content-col">
            {activeSection === "details" ? (
              <div className="card shadow-sm capa-config-saas__panel-card">
                <div className="card-body">
                  <h6 className="mb-1">Organisation details</h6>
                  <p className="text-muted small mb-3">
                    Configure organisation name, country, timezone, currency, and clinic
                    booking defaults. System timestamps use this timezone (summer/winter
                    handled automatically).
                  </p>
                  <div className="row g-3">
                    <div className="col-xl-4 col-lg-6">
                      <label className="form-label">Organisation name *</label>
                      <input
                        type="text"
                        className={`form-control ${errors.orgName ? "is-invalid" : ""}`}
                        value={orgName}
                        disabled={isBusy}
                        onChange={(e) => setOrgName(e.target.value)}
                      />
                      {errors.orgName ? (
                        <div className="invalid-feedback d-block">{errors.orgName}</div>
                      ) : null}
                    </div>
                    <div className="col-xl-4 col-lg-6">
                      <label className="form-label">Organisation country *</label>
                      <select
                        className={`form-control ${errors.countryIso2 ? "is-invalid" : ""}`}
                        value={countryIso2}
                        disabled={isBusy}
                        onChange={(e) => applyCountryDefaults(e.target.value)}
                      >
                        {countryOptions.map((opt) => (
                          <option key={opt.value} value={opt.value}>
                            {opt.label}
                          </option>
                        ))}
                      </select>
                      {errors.countryIso2 ? (
                        <div className="invalid-feedback d-block">{errors.countryIso2}</div>
                      ) : (
                        <div className="form-text">Dial code {dialCode}</div>
                      )}
                    </div>
                    <div className="col-xl-4 col-lg-6">
                      <label className="form-label">Organisation timezone *</label>
                      <select
                        className={`form-control ${errors.timeZoneId ? "is-invalid" : ""}`}
                        value={timeZoneId}
                        disabled={isBusy}
                        onChange={(e) => setTimeZoneId(e.target.value)}
                      >
                        {timeZoneOptions.map((opt) => (
                          <option key={opt.value} value={opt.value}>
                            {opt.label}
                          </option>
                        ))}
                      </select>
                      {errors.timeZoneId ? (
                        <div className="invalid-feedback d-block">{errors.timeZoneId}</div>
                      ) : (
                        <div className="form-text">
                          Filtered by organisation country. Used for system timestamps.
                        </div>
                      )}
                    </div>
                    <div className="col-xl-4 col-lg-6">
                      <label className="form-label">Currency</label>
                      <input
                        type="text"
                        className="form-control"
                        value={currencyLabel}
                        disabled
                        readOnly
                      />
                      <div className="form-text">
                        Bound to organisation country (not selectable separately).
                      </div>
                    </div>
                    <div className="col-xl-4 col-lg-6">
                      <label className="form-label">Clinic opening time *</label>
                      <input
                        type="time"
                        className={`form-control ${errors.startTime ? "is-invalid" : ""}`}
                        value={startTime}
                        disabled={isBusy}
                        onChange={(e) => setStartTime(e.target.value)}
                      />
                      {errors.startTime ? (
                        <div className="invalid-feedback d-block">{errors.startTime}</div>
                      ) : null}
                    </div>
                    <div className="col-xl-4 col-lg-6">
                      <label className="form-label">Clinic closing time *</label>
                      <input
                        type="time"
                        className={`form-control ${errors.endTime ? "is-invalid" : ""}`}
                        value={endTime}
                        disabled={isBusy}
                        onChange={(e) => setEndTime(e.target.value)}
                      />
                      {errors.endTime ? (
                        <div className="invalid-feedback d-block">{errors.endTime}</div>
                      ) : null}
                    </div>
                    <div className="col-xl-4 col-lg-6">
                      <label className="form-label">Default slot duration (minutes) *</label>
                      <select
                        className={`form-control ${errors.slotDuration ? "is-invalid" : ""}`}
                        value={slotDuration}
                        disabled={isBusy}
                        onChange={(e) => setSlotDuration(e.target.value)}
                      >
                        <option value="">Select slot</option>
                        {SLOT_OPTIONS.map((value) => (
                          <option key={value} value={value}>
                            {value}
                          </option>
                        ))}
                      </select>
                      {errors.slotDuration ? (
                        <div className="invalid-feedback d-block">{errors.slotDuration}</div>
                      ) : null}
                    </div>
                    <div className="col-xl-4 col-lg-6">
                      <label className="form-label">Default no. of booking per slot *</label>
                      <input
                        type="text"
                        maxLength={2}
                        className={`form-control ${errors.bookingsPerSlot ? "is-invalid" : ""}`}
                        value={bookingsPerSlot}
                        disabled={isBusy}
                        onChange={(e) =>
                          setBookingsPerSlot(e.target.value.replace(/\D/g, "").slice(0, 2))
                        }
                      />
                      {errors.bookingsPerSlot ? (
                        <div className="invalid-feedback d-block">{errors.bookingsPerSlot}</div>
                      ) : null}
                    </div>
                    <div className="col-xl-4 col-lg-6">
                      <label className="form-label">Study archive retention (years) *</label>
                      <input
                        type="text"
                        maxLength={3}
                        className={`form-control ${
                          errors.retentionPeriodYears ? "is-invalid" : ""
                        }`}
                        value={retentionPeriodYears}
                        disabled={isBusy}
                        onChange={(e) =>
                          setRetentionPeriodYears(
                            e.target.value.replace(/\D/g, "").slice(0, 3),
                          )
                        }
                      />
                      <div className="form-text">
                        After archiving a study, it can be unarchived within this
                        period (default 10 years).
                      </div>
                      {errors.retentionPeriodYears ? (
                        <div className="invalid-feedback d-block">
                          {errors.retentionPeriodYears}
                        </div>
                      ) : null}
                    </div>
                  </div>
                  {renderSaveButton()}
                </div>
              </div>
            ) : null}

            {SHOW_STUDY_LOCATIONS_TAB && activeSection === "locations" ? (
              <div className="card shadow-sm capa-config-saas__panel-card">
                <div className="card-body">
                  <h6 className="mb-1">Study locations</h6>
                  <p className="text-muted small mb-3">
                    Manage study sites and locations for your organisation.
                  </p>
                  <div className="table-responsive">
                    <table className="table table-bordered org-setup-saas__locations-table">
                      <thead>
                        <tr>
                          <th>Location</th>
                          <th style={{ width: "90px" }} />
                        </tr>
                      </thead>
                      <tbody>
                        {locations.map((row, index) => (
                          <tr key={row.key}>
                            <td>
                              <input
                                type="text"
                                className="form-control"
                                placeholder="Location name"
                                maxLength={200}
                                value={row.LocationName}
                                disabled={isBusy}
                                onChange={(e) => {
                                  const value = e.target.value;
                                  setLocations((prev) =>
                                    prev.map((item, i) =>
                                      i === index ? { ...item, LocationName: value } : item,
                                    ),
                                  );
                                }}
                              />
                            </td>
                            <td className="text-center">
                              <button
                                type="button"
                                className="btn btn-sm btn-link text-success p-1"
                                title="Add row"
                                disabled={isBusy}
                                onClick={() => handleAddLocationRow(index)}
                              >
                                <i className="fas fa-plus" />
                              </button>
                              {locations.length > 1 || row.LocationId ? (
                                <button
                                  type="button"
                                  className="btn btn-sm btn-link text-danger p-1"
                                  title="Remove"
                                  disabled={isBusy || deletingLocationId === row.LocationId}
                                  onClick={() => handleRemoveLocationRow(index)}
                                >
                                  <i
                                    className={`fas ${
                                      deletingLocationId === row.LocationId
                                        ? "fa-spinner fa-spin"
                                        : "fa-trash"
                                    }`}
                                  />
                                </button>
                              ) : null}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  {renderSaveButton()}
                </div>
              </div>
            ) : null}

            <div
              className={`card shadow-sm capa-config-saas__panel-card org-setup-ip-card${
                activeSection !== "networking" ? " d-none" : ""
              }`}
            >
              <div className="card-body">
                <OrganizationLoginIpSection
                  organizationId={organizationId}
                  staffId={staffId}
                  showAlert={showAlert}
                  isActive={activeSection === "networking"}
                  embedded
                />
              </div>
            </div>

            <div
              className={`card shadow-sm capa-config-saas__panel-card${
                activeSection !== "security" ? " d-none" : ""
              }`}
            >
              <div className="card-body">
                <OrganizationCredentialPolicySection
                  organizationId={organizationId}
                  editorStaffId={staffId}
                  showAlert={showAlert}
                  isActive={activeSection === "security"}
                />
              </div>
            </div>

            <div
              className={`card shadow-sm capa-config-saas__panel-card${
                activeSection !== "reminders" ? " d-none" : ""
              }`}
            >
              <div className="card-body">
                <OrganizationRemindersSection
                  organizationId={organizationId}
                  editorStaffId={staffId}
                  showAlert={showAlert}
                  isActive={activeSection === "reminders"}
                  organisationTimeZoneId={timeZoneId}
                  isSuperAdmin={isSessionSuperAdmin(userData)}
                />
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default OrganizationSetup;
